import React from 'react';
import { FormattedDate } from 'react-intl';
import { AuditLog } from '@/types/audit';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { diffJson } from 'diff';

interface AuditLogDetailsDialogProps {
  log: AuditLog;
}

const JsonDiff: React.FC<{ oldData: any; newData: any }> = ({ oldData, newData }) => {
  const differences = diffJson(oldData, newData);

  return (
    <pre className="text-sm bg-gray-50 p-2 rounded overflow-x-auto">
      {differences.map((part, index) => (
        <span
          key={index}
          className={
            part.added ? 'bg-green-100 text-green-800' : part.removed ? 'bg-red-100 text-red-800' : 'text-gray-600'
          }
        >
          {part.value}
        </span>
      ))}
    </pre>
  );
};

export const AuditLogDetailsDialog: React.FC<AuditLogDetailsDialogProps> = ({ log }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="link">View Details</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Audit Log Details</DialogTitle>
          <DialogDescription>
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium">Action</h4>
                  <p className="text-sm text-gray-500">{log.action}</p>
                </div>
                <div>
                  <h4 className="font-medium">Entity Type</h4>
                  <p className="text-sm text-gray-500">{log.entityType}</p>
                </div>
                <div>
                  <h4 className="font-medium">Entity ID</h4>
                  <p className="text-sm text-gray-500">{log.entityId}</p>
                </div>
                <div>
                  <h4 className="font-medium">Date</h4>
                  <p className="text-sm text-gray-500">
                    <FormattedDate value={log.createdAt} dateStyle="medium" timeStyle="short" />
                  </p>
                </div>
              </div>

              {(log.oldData || log.newData) && (
                <div>
                  <h4 className="font-medium mb-2">Changes</h4>

                  {log.oldData && log.newData ? (
                    <JsonDiff oldData={log.oldData} newData={log.newData} />
                  ) : log.oldData ? (
                    <div>
                      <p className="text-sm font-medium text-red-600 mb-1">Removed Data:</p>
                      <pre className="text-sm text-red-600 bg-red-50 p-2 rounded">
                        {JSON.stringify(log.oldData, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-green-600 mb-1">Added Data:</p>
                      <pre className="text-sm text-green-600 bg-green-50 p-2 rounded">
                        {JSON.stringify(log.newData, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {log.metadata && (
                <div>
                  <h4 className="font-medium">Metadata</h4>
                  <Card>
                    <CardContent className="p-4">
                      <pre className="text-sm text-gray-500 bg-gray-50 p-2 rounded">
                        {JSON.stringify(log.metadata, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
